import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Download, X, Smartphone, Share, Anchor } from 'lucide-react';

// Store prompt globally so it persists across component remounts
let globalDeferredPrompt = null;

// Export function to check if install is available
export const canInstallPWA = () => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone;
  return !isStandalone;
};

// Export function to get platform info
export const getPlatformInfo = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone;
  return { isIOS, isAndroid, isStandalone, hasPrompt: !!globalDeferredPrompt };
};

const InstallPrompt = ({ forceShow = false, onClose }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [showPrompt, setShowPrompt] = useState(forceShow);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Chrome/Edge — works on desktop too)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      setDeferredPrompt(e);

      // Show prompt automatically (mobile OR desktop) if not dismissed recently
      if (!forceShow) {
        const dismissedAt = localStorage.getItem('install_prompt_dismissed_at');
        const dayAgo = Date.now() - 24 * 60 * 60 * 1000;

        if (!dismissedAt || parseInt(dismissedAt) < dayAgo) {
          setTimeout(() => setShowPrompt(true), 2000);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // If forceShow, show immediately
    if (forceShow && !standalone) {
      setShowPrompt(true);
    } else if (globalDeferredPrompt && !standalone && !forceShow) {
      const dismissedAt = localStorage.getItem('install_prompt_dismissed_at');
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      if (!dismissedAt || parseInt(dismissedAt) < dayAgo) {
        setTimeout(() => setShowPrompt(true), 2000);
      }
    }

    // For iOS on mobile, show after delay if not installed
    if (iOS && !standalone && mobile && !forceShow) {
      const dismissedAt = localStorage.getItem('install_prompt_dismissed_ios_at');
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      
      if (!dismissedAt || parseInt(dismissedAt) < dayAgo) {
        setTimeout(() => setShowPrompt(true), 2500);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [forceShow]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        handleClose();
        globalDeferredPrompt = null;
      }
      setDeferredPrompt(null);
    }
  };

  const handleClose = useCallback(() => {
    setShowPrompt(false);
    if (onClose) onClose();
  }, [onClose]);

  const handleDismiss = () => {
    localStorage.setItem('install_prompt_dismissed_at', Date.now().toString());
    if (isIOS) {
      localStorage.setItem('install_prompt_dismissed_ios_at', Date.now().toString());
    }
    handleClose();
  };

  const handleLater = () => {
    // Will show again after 6 hours
    localStorage.setItem('install_prompt_dismissed_at', (Date.now() - 18 * 60 * 60 * 1000).toString());
    handleClose();
  };

  // Don't show if already installed or no prompt available.
  // (Previously blocked on desktop — now we honor `beforeinstallprompt` on PC Chrome/Edge too.)
  if (isStandalone || !showPrompt) return null;
  // iOS-specific path only makes sense on iOS. On desktop without a native prompt, hide.
  if (!isMobile && !deferredPrompt && !forceShow) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleLater}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            data-testid="install-prompt-backdrop"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed left-4 right-4 top-1/2 -translate-y-1/2 z-[101] max-w-sm mx-auto"
            data-testid="install-prompt-modal"
          >
            <div className="bg-card border border-primary/30 rounded-2xl overflow-hidden shadow-2xl">
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-6 pb-4 relative">
                <button
                  onClick={handleDismiss}
                  className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-full transition-colors"
                  data-testid="install-prompt-close"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                    <Anchor className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">L'Équipage Odys</h2>
                    <p className="text-sm text-muted-foreground">Installe l'application</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 pt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  {isIOS 
                    ? "Ajoute L'Équipage à ton écran d'accueil pour un accès rapide et une meilleure expérience !"
                    : isMobile
                      ? "Installe l'app sur ton téléphone pour un accès rapide, des notifications push et une expérience optimale."
                      : "Installe l'app sur ton ordinateur pour un accès en un clic depuis ton bureau, des notifications natives et une expérience optimale."
                  }
                </p>

                {isIOS ? (
                  <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
                    <p className="font-semibold text-sm flex items-center gap-2">
                      <Share className="w-4 h-4 text-primary" />
                      Comment installer :
                    </p>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                        <span>Appuie sur le bouton <strong className="text-foreground">Partager</strong> en bas de Safari</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                        <span>Fais défiler et appuie sur <strong className="text-foreground">"Sur l'écran d'accueil"</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                        <span>Appuie sur <strong className="text-foreground">Ajouter</strong></span>
                      </li>
                    </ol>
                    <Button 
                      className="w-full mt-2" 
                      variant="outline"
                      onClick={handleDismiss}
                    >
                      J'ai compris
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Button 
                      onClick={handleInstall} 
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base"
                      data-testid="install-button"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Installer maintenant
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={handleLater}
                      className="w-full text-muted-foreground"
                    >
                      Plus tard
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
